import React, { useRef, useState } from 'react';
import axios from 'axios';
import { LoaderType, Loading } from '@/layouts/hettwer/loading';
import { KeenIcon } from '@/components';
import { useNavigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { useStatistic } from '@/contexts/StatisticContext.tsx';

interface Entity {
  p_id: string;
  name: string;
}

interface UploadTextProps {
  parentEntity?: Entity | null;
  objectEntity?: Entity | null;
  entityType: 'candidate' | 'job' | 'offer' | 'new_offer' | 'existing_offer';
  mode: 'create' | 'addSource';
  onSuccess?: () => void;
}

const UploadTextWithDictation: React.FC<UploadTextProps> = ({
  parentEntity = null,
  objectEntity = null,
  entityType,
  mode,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [text, setText] = useState('');

  // Hilfe ein-/ausblenden
  const [showHelp, setShowHelp] = useState(false);

  // Sprachindikator
  const [audioLevel, setAudioLevel] = useState(0);
  const [voiceDetected, setVoiceDetected] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const activeMimeTypeRef = useRef<string | null>(null);

  // Audio-Analyse
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const rafRef = useRef<number | null>(null);

  const navigate = useNavigate();
  const { refreshStatistic } = useStatistic();

  // ---------------- API MAP ----------------
  const apiUrlMap = {
    candidate: {
      create: `${import.meta.env.VITE_APP_API_URL}/candidate/create/text`,
      addSource: `${import.meta.env.VITE_APP_API_URL}/candidate/add-source/text`
    },
    job: {
      create: `${import.meta.env.VITE_APP_API_URL}/job/create/text`,
      addSource: `${import.meta.env.VITE_APP_API_URL}/job/add-source/text`
    },
    offer: {
      create: `${import.meta.env.VITE_APP_API_URL}/offer/create/text`,
      addSource: `${import.meta.env.VITE_APP_API_URL}/offer/add-source/text`
    },
    new_offer: {
      create: `${import.meta.env.VITE_APP_API_URL}/new-offer/create/text`,
      addSource: `${import.meta.env.VITE_APP_API_URL}/offer/add-source/text`
    },
    existing_offer: {
      create: `${import.meta.env.VITE_APP_API_URL}/existing-offer/create/file`,
      addSource: `${import.meta.env.VITE_APP_API_URL}/existing-offer/add-source/file`
    }
  };

  const API_TEXT_UPLOAD = apiUrlMap[entityType][mode];

  let uploadUrl = API_TEXT_UPLOAD;
  if (mode === 'create' && parentEntity?.p_id && entityType !== 'offer') {
    uploadUrl = `${API_TEXT_UPLOAD}/${parentEntity.p_id}`;
  } else if (mode === 'addSource' && objectEntity?.p_id) {
    uploadUrl = `${API_TEXT_UPLOAD}/${objectEntity.p_id}`;
  }

  // ---------------- MIME TYPE ----------------
  const getBestSupportedMimeType = (): string => {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/wav'];
    for (const type of candidates) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    throw new Error('Kein unterstütztes Audioformat gefunden');
  };

  // ---------------- AUFNAHME ----------------
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      const mimeType = getBestSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });

      audioChunksRef.current = [];
      activeMimeTypeRef.current = mimeType;
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.start();

      // Sprachpegel
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();

      analyser.fftSize = 256;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      dataArrayRef.current = dataArray;

      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;

        setAudioLevel(avg);
        setVoiceDetected(avg > 20);

        rafRef.current = requestAnimationFrame(updateLevel);
      };

      updateLevel();
      setRecording(true);
    } catch (err) {
      console.error('[Dictation] Aufnahme fehlgeschlagen:', err);
      enqueueSnackbar('Der Zugriff auf das Mikrofon ist nicht möglich.', {
        variant: 'solid',
        state: 'danger'
      });
    }
  };

  const stopRecording = () => {
    if (!mediaRecorderRef.current) return;

    setRecording(false);
    setLoading(true);

    const recorder = mediaRecorderRef.current;

    recorder.onstop = async () => {
      try {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: activeMimeTypeRef.current || 'audio/webm'
        });

        const formData = new FormData();
        formData.append('audio', audioBlob);

        const response = await axios.post(
          `${import.meta.env.VITE_APP_API_URL}/dictation`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authToken')}`
            }
          }
        );

        const dictatedText = response.data.text;
        setText((prev) => (prev.trim() ? `${prev.trim()}\n\n${dictatedText}` : dictatedText));
      } catch (err) {
        console.error('[Dictation] Upload fehlgeschlagen', err);
        enqueueSnackbar('Die Spracherkennung ist fehlgeschlagen.', {
          variant: 'solid',
          state: 'danger'
        });
      } finally {
        setLoading(false);
        audioChunksRef.current = [];
      }
    };

    recorder.stop();

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    audioContextRef.current?.close();

    setAudioLevel(0);
    setVoiceDetected(false);
  };

  const toggleRecording = () => {
    recording ? stopRecording() : startRecording();
  };

  // ---------------- ABSENDEN ----------------
  const handleSubmit = async () => {
    if (text.trim().length < 10) {
      enqueueSnackbar(
        'Bitte geben Sie mindestens 10 Zeichen Text ein oder sprechen Sie diese ein.',
        {
          variant: 'solid',
          state: 'danger'
        }
      );
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        uploadUrl,
        { text },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }
      );

      const newPath = `/offer/${response.data.p_id}`;
      window.location.pathname === newPath ? onSuccess?.() : navigate(newPath);

      setText('');
      await refreshStatistic();

      enqueueSnackbar(
        'Der Text wurde erfolgreich verarbeitet und für die Angebotserstellung genutzt.',
        {
          variant: 'solid',
          state: 'success'
        }
      );
    } catch (err) {
      console.error('[UploadText] Übertragung fehlgeschlagen:', err);
      enqueueSnackbar('Der Text konnte nicht verarbeitet werden.', {
        variant: 'solid',
        state: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  // ---------------- RENDER ----------------
  return (
    <div className="card pb-2.5">
      <div className="card-header" id="upload_text">
        <h3 className="card-title">Neues Angebot auf Basis vorhandener Daten erstellen</h3>
      </div>

      <div className="card-body grid gap-5">
        {!loading && (
          <div className="flex flex-col gap-4">
            {/* Hinweis zur Angebotserstellung */}
            <details className="rounded border bg-white p-4">
              <summary className="cursor-pointer text-2sm font-medium text-gray-800">
                Hinweis zur Angebotserstellung
              </summary>

              <div className="mt-4 space-y-4 text-2sm text-gray-800 leading-relaxed">
                <p>
                  <strong>Tipp:</strong> Sie können den Diktier-Button mehrfach verwenden. Der
                  erkannte Text wird jeweils an den bestehenden Inhalt angehängt. So können Sie
                  Informationen Stück für Stück ergänzen.
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                      Projektdaten & Felder
                    </h4>
                    <p>
                      Nennen Sie einfach die Begriffe, die KI ordnet sie zu (z.B. Kundendaten,
                      Ansprechpartner, E-Mail, Gültigkeit, Projektstandort).
                      <br />
                      <span className="italic">
                        Beispiel: "Der Kunde ist Müller GmbH, Standort Berlin."
                      </span>
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                      Strukturierung von Positionen
                    </h4>
                    <p>
                      Sagen Sie der KI, wo eine Position anfängt und aufhört. Nennen Sie dabei
                      Titel, Menge, Einheit und Preise.
                      <br />
                      <span className="italic">
                        Beispiel: "Neue Position: Lackierung, 50 Quadratmeter, Preis 20 Euro.
                        [Beschreibung...] Position Ende."
                      </span>
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                      Gezielte KI-Anweisungen
                    </h4>
                    <p>
                      Geben Sie Anweisungen zur Bearbeitung: "Übernimm den Preis 1 zu 1 aus dem
                      Dokument", "Schreibe meine Beschreibung wesentlich kürzer" oder "Berechne das
                      Gesamtgewicht basierend auf 40kg pro Stück".
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1 text-sm">
                      Technische Details & Maße
                    </h4>
                    <p>
                      Sie können auch Felder wie Material, Gewerk, Länge, Breite, Höhe oder Volumen
                      direkt einsprechen. Diese werden im Hintergrund verarbeitet und korrekt
                      zugeordnet.
                    </p>
                  </div>
                </div>
              </div>
            </details>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Geben Sie hier den Text ein oder sprechen Sie ihn ein."
              className="resize-none border border-gray-400 p-3 rounded-md text-sm w-full h-40 focus:outline-none"
            />

            <div className="flex justify-between items-center pt-2.5">
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleRecording}
                  className={`btn btn-sm ${recording ? 'btn-danger' : 'btn-primary'}`}
                >
                  <KeenIcon icon="message-text" />
                  {recording ? ' Aufnahme stoppen' : ' Text einsprechen'}
                </button>

                {recording && (
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-gray-200 rounded overflow-hidden">
                      <div
                        className={`h-full transition-all duration-75 ${
                          voiceDetected ? 'bg-primary' : 'bg-gray-400'
                        }`}
                        style={{ width: `${Math.min(audioLevel * 3, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={handleSubmit}
                className="btn btn-sm btn-primary"
                disabled={text.trim().length < 10}
              >
                <KeenIcon icon="plus" /> Angebot erstellen
              </button>
            </div>
          </div>
        )}

        {loading && (
          <Loading
            loadingText={recording ? 'Aufnahme läuft …' : 'Die Daten werden verarbeitet …'}
            loaderType={LoaderType.LOADING_FILES}
            loading={loading}
            delay={0}
            sizePercentage={130}
          />
        )}
      </div>
    </div>
  );
};

export { UploadTextWithDictation };
